<template>
   <v-select
      ref="select"
      class="tag-select"
      :model-value="modelValue"
      :options="tags"
      label="name"
      :reduce="tag => tag.id"
      multiple
      :selectable="tag => !modelValue.includes(tag.id)"
      :placeholder="placeholderText"
      @update:model-value="$emit('update:modelValue', $event)"
      @search="onSearch"
   >
      <!-- Each tag with its colour -->
      <template #option="tag">
         <span class="tag-dot" :style="{ background: tag.color }"></span> {{ tag.name }}
      </template>
      <template #selected-option="tag">
         <span class="tag-dot" :style="{ background: tag.color }"></span> {{ tag.name }}
      </template>

      <template #no-options>
         <span v-if="!canCreate">{{ tags.length ? "No matching tag." : "No tags yet." }}</span>
      </template>

      <!-- "+ Add tag" pinned at the bottom when the typed name doesn't exist -->
      <template #list-footer>
         <!-- Nothing typed yet: tell the user how to add a tag -->
         <li v-if="showHint" class="tag-hint" @mousedown.prevent>
            <i class="fa fa-plus me-1"></i> Type a name to add a new tag
         </li>
         <!-- "Add tag" and its error stay together at the bottom of the list -->
         <li v-if="canCreate || error" class="tag-footer" @mousedown.prevent>
            <!-- Name typed that doesn't exist: the real "Add tag" -->
            <div v-if="canCreate" class="tag-add" @click="addTag">
               <i class="fa fa-plus me-1"></i>
               {{ creating ? "Adding…" : `Add tag "${searchText.trim()}"` }}
            </div>
            <!-- Same colours on every theme (Bootstrap alert-danger) -->
            <div v-if="error" class="tag-error" role="alert">
               <i class="fa fa-exclamation-triangle me-1"></i>{{ error }}
            </div>
         </li>
      </template>
   </v-select>
</template>

<script>
// Tags box: pick several tags, or type a new name and choose "Add tag".
//   <TagSelect v-model="formInputs.tags" :tags="tagOptions" api="/crud/api_crud.php"
//              :allow-create="$hasPermission('crudtest.add')" @created="tag => tagOptions.push(tag)" />
// v-model = list of tag ids. tags = [{ id, name, color }]. api must answer action=createTag.
import axios from "axios";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
   name: "TagSelect",
   components: { vSelect },
   props: {
      modelValue: { type: Array, default: () => [] },
      tags: { type: Array, default: () => [] },
      api: { type: String, default: "" },
      allowCreate: { type: Boolean, default: true },
      placeholder: { type: String, default: "" }, // empty = automatic (see placeholderText)
   },
   emits: ["update:modelValue", "created"],
   data() {
      return {
         searchText: "",
         creating: false,
         error: "",
      };
   },
   computed: {
      // Users who can add tags are told they can type a new one
      placeholderText() {
         if (this.placeholder) return this.placeholder;
         return this.allowCreate && this.api ? "Choose a tag or type a new one" : "Choose tags";
      },
      // Grey hint at the bottom while nothing is typed (only where tags can be created)
      showHint() {
         return this.allowCreate && !!this.api && !this.searchText.trim();
      },
      // Show "Add tag" only for a name that doesn't exist yet
      canCreate() {
         const text = this.searchText.trim().toLowerCase();
         if (!this.allowCreate || !this.api || !text) return false;
         return !this.tags.some((tag) => tag.name.toLowerCase() === text);
      },
   },
   methods: {
      onSearch(text) {
         this.searchText = text || "";
         this.error = "";
      },
      async addTag() {
         if (this.creating || !this.canCreate) return;
         this.creating = true;
         this.error = "";

         try {
            const formData = new FormData();
            formData.append("action", "createTag");
            formData.append("name", this.searchText.trim());

            const response = await axios.post(this.api, formData, this.$handleConfig());

            if (response.data.status === "success") {
               const tag = response.data.results.tag;
               this.$emit("created", tag); // the page adds it to its list first
               if (!this.modelValue.includes(tag.id)) {
                  this.$emit("update:modelValue", [...this.modelValue, tag.id]);
               }
               // Empty the typing box
               this.searchText = "";
               if (this.$refs.select) this.$refs.select.search = "";
            } else {
               this.error = response.data.message || "Could not add the tag.";
            }
         } catch (e) {
            this.error = "Could not add the tag. Please try again.";
         } finally {
            this.creating = false;
         }
      },
   },
};
</script>

<style scoped>
/* Tags already chosen don't show in the list */
.tag-select :deep(.vs__dropdown-option--selected),
.tag-select :deep(.vs__dropdown-option--disabled) {
   display: none;
}
.tag-dot {
   display: inline-block;
   width: 10px;
   height: 10px;
   border-radius: 50%;
   margin-right: 4px;
   vertical-align: middle;
}
.tag-hint {
   position: sticky;
   bottom: 0;
   z-index: 2;
   background-color: #f8f9fa !important;
   border-top: 1px solid #ddd;
   padding: 9px 20px;
   color: #6c757d;
   font-style: italic;
   cursor: default;
   list-style: none;
}
/* Holds "Add tag" and the error: both stay at the bottom while the list scrolls */
.tag-footer {
   position: sticky;
   bottom: 0;
   z-index: 2;
   list-style: none;
}
.tag-add {
   background-color: #ffffff !important;
   border-top: 1px solid #ddd;
   padding: 9px 20px;
   font-weight: 600;
   color: #198754;
   cursor: pointer;
   list-style: none;
}
.tag-add:hover {
   background-color: #198754 !important;
   color: #fff;
}
/* Light red box with dark red text: readable on dark, light and glass themes */
.tag-error {
   background-color: #f8d7da !important;
   color: #842029;
   border-top: 1px solid #f5c2c7;
   padding: 8px 20px;
   font-size: 0.85rem;
   cursor: default;
}
</style>